import type { JsonObject, ToolDef } from "@fabric-harness/sdk";
import type { CatalogsClient } from "@databricks/sdk-uc-catalogs/v1";
import type { GrantsClient } from "@databricks/sdk-uc-grants/v1";
import type { SchemasClient } from "@databricks/sdk-uc-schemas/v1";
import type { VolumesClient } from "@databricks/sdk-uc-volumes/v1";

export interface DatabricksUnityCatalogAdminClients {
  catalogs: Pick<CatalogsClient, "createCatalog" | "getCatalog" | "deleteCatalog">;
  schemas: Pick<SchemasClient, "createSchema" | "getSchema" | "deleteSchema">;
  volumes: Pick<VolumesClient, "createVolume" | "getVolume" | "deleteVolume">;
  grants: Pick<GrantsClient, "getPermissions" | "updatePermissions">;
}

export type DatabricksSecurableType =
  | "catalog"
  | "schema"
  | "table"
  | "volume"
  | "function"
  | "model";
export interface DatabricksPermissionChange {
  principal: string;
  add?: string[];
  remove?: string[];
}
const MANAGED_PROPERTY = "fabric-harness.managed";

export class DatabricksUnityCatalogAdmin {
  constructor(private readonly clients: DatabricksUnityCatalogAdminClients) {}
  getPermissions(securableType: DatabricksSecurableType, fullName: string): Promise<unknown> {
    return this.clients.grants.getPermissions({
      securableType: securableType as never,
      securableFullName: fullName,
    });
  }
  updatePermissions(
    securableType: DatabricksSecurableType,
    fullName: string,
    changes: DatabricksPermissionChange[],
  ): Promise<unknown> {
    return this.clients.grants.updatePermissions({
      securableType: securableType as never,
      securableFullName: fullName,
      changes: changes as never,
    });
  }
  createCatalog(input: {
    name: string;
    comment?: string;
    storageRoot?: string;
    properties?: Record<string, string>;
  }): Promise<unknown> {
    return this.clients.catalogs.createCatalog({
        name: input.name,
        ...(input.comment ? { comment: input.comment } : {}),
        ...(input.storageRoot ? { storageRoot: input.storageRoot } : {}),
        properties: { ...(input.properties ?? {}), [MANAGED_PROPERTY]: "true" },
      });
  }
  createSchema(input: {
    catalogName: string;
    name: string;
    comment?: string;
    properties?: Record<string, string>;
  }): Promise<unknown> {
    return this.clients.schemas.createSchema({
        name: input.name,
        catalogName: input.catalogName,
        ...(input.comment ? { comment: input.comment } : {}),
        properties: { ...(input.properties ?? {}), [MANAGED_PROPERTY]: "true" },
      });
  }
  createVolume(input: {
    catalogName: string;
    schemaName: string;
    name: string;
    volumeType?: "MANAGED" | "EXTERNAL";
    storageLocation?: string;
    comment?: string;
  }): Promise<unknown> {
    return this.clients.volumes.createVolume({
        catalogName: input.catalogName,
        schemaName: input.schemaName,
        name: input.name,
        volumeType: input.volumeType ?? "MANAGED",
        ...(input.storageLocation ? { storageLocation: input.storageLocation } : {}),
        comment: `[fabric-harness:managed]${input.comment ? ` ${input.comment}` : ""}`,
      });
  }
  deleteCatalog(name: string, force = false): Promise<unknown> {
    return this.clients.catalogs.deleteCatalog({ nameArg: name, force });
  }
  deleteSchema(fullName: string, force = false): Promise<unknown> {
    return this.clients.schemas.deleteSchema({ fullNameArg: fullName, force });
  }
  deleteVolume(fullName: string): Promise<unknown> {
    return this.clients.volumes.deleteVolume({ fullNameArg: fullName });
  }
  async deleteManaged(kind: "catalog" | "schema" | "volume", fullName: string): Promise<unknown> {
    const row = kind === "catalog"
      ? await this.clients.catalogs.getCatalog({ nameArg: fullName })
      : kind === "schema"
        ? await this.clients.schemas.getSchema({ fullNameArg: fullName })
        : await this.clients.volumes.getVolume({ fullNameArg: fullName });
    const properties = "properties" in row
      ? row.properties as Record<string, unknown> | undefined
      : undefined;
    const managed =
      kind === "volume"
        ? typeof row.comment === "string" && row.comment.startsWith("[fabric-harness:managed]")
        : properties?.[MANAGED_PROPERTY] === "true";
    if (!managed)
      throw new Error(`databricks UC: refusing to delete unmanaged ${kind} "${fullName}".`);
    if (kind === "catalog") return this.deleteCatalog(fullName, false);
    if (kind === "schema") return this.deleteSchema(fullName, false);
    return this.deleteVolume(fullName);
  }
}

export function databricksUnityCatalogAdminTools(
  client: DatabricksUnityCatalogAdminClients,
  options: { destructive?: boolean } = {},
): ToolDef[] {
  const api = new DatabricksUnityCatalogAdmin(client);
  const tools: ToolDef[] = [
    tool(
      "databricks_grant",
      "write",
      permissionSchema(),
      [{ kind: "securable", path: "/fullName", catalogQualified: true }],
      (i) =>
        api.updatePermissions(i.securableType as DatabricksSecurableType, i.fullName as string, [
          { principal: i.principal as string, add: i.privileges as string[] },
        ]),
    ),
    tool(
      "databricks_revoke",
      "write",
      permissionSchema(),
      [{ kind: "securable", path: "/fullName", catalogQualified: true }],
      (i) =>
        api.updatePermissions(i.securableType as DatabricksSecurableType, i.fullName as string, [
          { principal: i.principal as string, remove: i.privileges as string[] },
        ]),
    ),
    tool(
      "databricks_create_catalog",
      "write",
      catalogSchema(),
      [{ kind: "catalog", path: "/name", catalogQualified: true }],
      (i) => api.createCatalog(i as never),
    ),
    tool(
      "databricks_create_schema",
      "write",
      schemaSchema(),
      [
        { kind: "catalog", path: "/catalogName", catalogQualified: true },
        { kind: "schema", path: "/name" },
      ],
      (i) => api.createSchema(i as never),
    ),
    tool(
      "databricks_create_volume",
      "write",
      volumeSchema(),
      [
        { kind: "catalog", path: "/catalogName", catalogQualified: true },
        { kind: "schema", path: "/schemaName" },
        { kind: "volume", path: "/name" },
      ],
      (i) => api.createVolume(i as never),
    ),
  ];
  if (options.destructive)
    tools.push(
      tool(
        "databricks_delete_uc_object",
        "write",
        deleteSchema(),
        [{ kind: "securable", path: "/fullName", catalogQualified: true }],
        (i) => api.deleteManaged(i.kind as never, i.fullName as string),
      ),
    );
  return tools;
}

function tool(
  name: string,
  effect: "write",
  inputSchema: JsonObject,
  resources: JsonObject[],
  execute: (input: JsonObject) => unknown,
): ToolDef {
  return {
    name,
    description: name.replaceAll("_", " "),
    inputSchema,
    metadata: {
      effect,
      provider: "databricks",
      service: "unity-catalog",
      governance: { resources },
    },
    execute,
  };
}
function permissionSchema(): JsonObject {
  return {
    type: "object",
    additionalProperties: false,
    properties: {
      securableType: { type: "string" },
      fullName: { type: "string" },
      principal: { type: "string" },
      privileges: { type: "array", items: { type: "string" } },
    },
    required: ["securableType", "fullName", "principal", "privileges"],
  };
}
function catalogSchema(): JsonObject {
  return {
    type: "object",
    additionalProperties: false,
    properties: {
      name: { type: "string" },
      comment: { type: "string" },
      storageRoot: { type: "string" },
      properties: { type: "object", additionalProperties: { type: "string" } },
    },
    required: ["name"],
  };
}
function schemaSchema(): JsonObject {
  return {
    type: "object",
    additionalProperties: false,
    properties: {
      catalogName: { type: "string" },
      name: { type: "string" },
      comment: { type: "string" },
      properties: { type: "object", additionalProperties: { type: "string" } },
    },
    required: ["catalogName", "name"],
  };
}
function volumeSchema(): JsonObject {
  return {
    type: "object",
    additionalProperties: false,
    properties: {
      catalogName: { type: "string" },
      schemaName: { type: "string" },
      name: { type: "string" },
      volumeType: { type: "string", enum: ["MANAGED", "EXTERNAL"] },
      storageLocation: { type: "string" },
      comment: { type: "string" },
    },
    required: ["catalogName", "schemaName", "name"],
  };
}
function deleteSchema(): JsonObject {
  return {
    type: "object",
    additionalProperties: false,
    properties: {
      kind: { type: "string", enum: ["catalog", "schema", "volume"] },
      fullName: { type: "string" },
    },
    required: ["kind", "fullName"],
  };
}
